/**
 * dimensionConverter — DIMENSION → CadPrimitive[] (PDF-5b).
 *
 * REUSES the pure scene adapter `scene3d/dimensions/dimensionToPrimitives`
 * rather than re-deriving the per-subtype geometry (CLAUDE.md PRIORITY #1 —
 * full fidelity, no simplification): the adapter already ports the SVG
 * renderer's Linear / Aligned / Angular / Diameter / Radius / Ordinate
 * decomposition 1:1, including DIMSE1/2 extension-line gating, DIMBLK arrow vs
 * tick dispatch, DIMEXO / DIMEXE / DIMGAP and annotative DIMVAR overrides.
 *
 * Pipeline:
 *   1. typed `DimensionEntity` → `EntityPreview` property-bag via the canonical
 *      bridge `buildShellEntityPreview` (state/dxfGeometry). This resolves the
 *      referenced DIMENSION style's DIMVARs (from `ctx.dimensionStyles`) into
 *      the property bag the adapter's `readDimStyle` reads, so DIMTXT / DIMASZ /
 *      DIMBLK / DIMDEC reach the plot.
 *   2. call `dimensionToPrimitives` → `RenderableEntity.geometry.primitives`
 *      (a `DimensionPrimitive[]`).
 *   3. UN-OFFSET every coordinate. The adapter bakes `applyOffset` (subtract the
 *      session coordinate origin) into every Vec3; the PDF backend wants RAW
 *      world mm (the page / VP transform does its own near-origin localize,
 *      §4.1). We `unapplyOffset` (add the origin back) so the result is RAW —
 *      identity when no origin is set (jsdom tests), correct when one is.
 *   4. map each `DimensionPrimitive` kind → `CadPrimitive`:
 *        extension-line / dim-line / leader → stroked line `PathPrimitive`;
 *        arc                                → stroked bezier `PathPrimitive`;
 *        arrow                              → FILL-ONLY closed `PathPrimitive`
 *                                             (solid arrowhead polygon) or, for
 *                                             open variants, a stroked outline;
 *        tick                               → stroked oblique/arch slash;
 *        text                               → `TextRunPrimitive` (measurement).
 *
 * Colour: the adapter resolves the dimension's BYLAYER/BYBLOCK chain to a
 * concrete `colorHex` and stamps it on every sub-primitive — we convert that to
 * a plot RGB (white→black tweak) via `hexToPlotRgb`. Lineweight comes from the
 * entity's plot style (`resolvePlotStyle`), matching every other converter.
 */

import type {
  DimensionEntity,
  DimensionStyle,
} from "@dxf-ai-editor/drawing-model";

import { buildShellEntityPreview } from "../../../state/dxfGeometry";
import { unapplyOffset } from "../../../scene3d/coordinateOrigin";
import { dimensionToPrimitives } from "../../../scene3d/dimensions/dimensionToPrimitives";
import type {
  DimensionPrimitive,
  Vec3,
} from "../../../scene3d/types";
import type { AdapterContext } from "../../../scene3d/adapters/adapterContext";

import { hexToPlotRgb } from "../pdfTransform";
import type {
  CadConvertContext,
  CadPrimitive,
  PathPrimitive,
  PathSegment,
  Point2,
  PlotStyle,
} from "../cadPrimitive";
import { circularArcToBeziers, ellipseToBeziers } from "./arcBezier";
import { resolvePlotStyle } from "./resolveStyle";
import {
  arrowFillPolygon,
  arrowOutlinePolyline,
  isFilledArrow,
  tickPolyline,
} from "./arrowGeometry";

/** Map an offset-world adapter Vec3 back to a RAW world {@link Point2}. */
function toRawPoint(v: Vec3): Point2 {
  const w = unapplyOffset(v);
  return { x: w.x, y: w.y };
}

/** DXF justification codes for the adapter's `TextAlignment` strings. */
function alignmentToCodes(alignment: string): {
  hJust: number;
  vJust: number;
} {
  switch (alignment) {
    case "baseline-left":
      return { hJust: 0, vJust: 0 };
    case "baseline-center":
      return { hJust: 1, vJust: 0 };
    case "baseline-right":
      return { hJust: 2, vJust: 0 };
    case "bottom-left":
      return { hJust: 0, vJust: 1 };
    case "bottom-center":
      return { hJust: 1, vJust: 1 };
    case "bottom-right":
      return { hJust: 2, vJust: 1 };
    case "middle-left":
      return { hJust: 0, vJust: 2 };
    case "middle-center":
      return { hJust: 1, vJust: 2 };
    case "middle-right":
      return { hJust: 2, vJust: 2 };
    case "top-left":
      return { hJust: 0, vJust: 3 };
    case "top-center":
      return { hJust: 1, vJust: 3 };
    case "top-right":
      return { hJust: 2, vJust: 3 };
    default:
      // `fit` / `aligned` have no vertical box; treat as baseline-center (the
      // adapter only ever emits baseline-center for dimension text).
      return { hJust: 1, vJust: 0 };
  }
}

/** A stroked open polyline `PathPrimitive` from an ordered point list. */
function strokedPath(
  entityId: string,
  layer: string,
  points: Point2[],
  style: PlotStyle,
): PathPrimitive | null {
  if (points.length < 2) return null;
  const segments: PathSegment[] = [];
  for (let i = 1; i < points.length; i++) {
    segments.push({ kind: "line", to: points[i]! });
  }
  return {
    type: "path",
    entityId,
    layer,
    start: points[0]!,
    segments,
    closed: false,
    fill: "none",
    style,
  };
}

/**
 * Convert a DIMENSION to its CadPrimitive list. Returns `[]` only when the
 * adapter produced no primitives (degenerate dimension).
 */
export function dimensionToPdfPrimitives(
  entity: DimensionEntity,
  ctx: CadConvertContext,
): CadPrimitive[] {
  // 1. typed entity → EntityPreview via the canonical bridge.
  const dimStylesByName = new Map<string, DimensionStyle>();
  for (const s of ctx.dimensionStyles ?? []) dimStylesByName.set(s.name, s);
  // blockRecords/blocks are unused by the DIMENSION preview builder (dimensions
  // carry no block refs) — pass the conversion context's block map so the
  // signature is satisfied without an empty fabricated record.
  const preview = buildShellEntityPreview(entity, dimStylesByName, {}, ctx.blocks);

  // 2. call the pure scene adapter. cameraZoom=1 → tessellation LOD floor
  //    (irrelevant: arcs are emitted as exact beziers downstream).
  const adapterCtx: AdapterContext = {
    layerColors: ctx.layerColors,
    blockColor: ctx.blockColor,
    cameraZoom: 1,
    zOrderIndex: 0,
  };
  const renderable = dimensionToPrimitives(preview, adapterCtx);
  if (renderable.geometry.kind !== "dimension") return [];
  const primitives = renderable.geometry.primitives;

  // The entity's plot style (lineweight + linetype + colour) is the fallback
  // pen; per-primitive colour overrides come from the adapter's `colorHex`.
  const baseStyle = resolvePlotStyle(entity, ctx);
  // PDF-6.3b — CTB/STB do-not-plot honour. The dimension would otherwise emit
  // a stack of stroked + filled sub-primitives; we short-circuit the whole lot.
  if (baseStyle.skip) return [];
  // PDF-6.2a: every dimension sub-primitive files under the DIMENSION's own
  // layer (the adapter's geometry has no independent per-part layer; AutoCAD
  // dimensions live wholly on one layer).
  const layer = entity.layer ?? "0";

  const out: CadPrimitive[] = [];
  for (const p of primitives) {
    pushPrimitive(out, entity.id, layer, p, baseStyle);
  }
  return out;
}

function styleWithColor(base: PlotStyle, colorHex: string): PlotStyle {
  return { ...base, color: hexToPlotRgb(colorHex) };
}

function pushPrimitive(
  out: CadPrimitive[],
  entityId: string,
  layer: string,
  p: DimensionPrimitive,
  baseStyle: PlotStyle,
): void {
  switch (p.kind) {
    case "extension-line":
    case "dim-line":
    case "leader": {
      const path = strokedPath(
        entityId,
        layer,
        [toRawPoint(p.a), toRawPoint(p.b)],
        styleWithColor(baseStyle, p.colorHex),
      );
      if (path) out.push(path);
      return;
    }
    case "arc": {
      const c = toRawPoint(p.center);
      // The adapter stores start/end angles (radians, CCW). DXF/arc convention
      // walks CCW from start to end — reuse the shared arc→bezier exact cubics.
      const { start, segments } = circularArcToBeziers(
        c.x,
        c.y,
        p.radius,
        p.startAngle,
        p.endAngle,
        true,
      );
      out.push({
        type: "path",
        entityId,
        layer,
        start,
        segments,
        closed: false,
        fill: "none",
        style: styleWithColor(baseStyle, p.colorHex),
      });
      return;
    }
    case "elliptic-arc": {
      // Codex PR #784 round 12 P2 — the affine image of an `arc` under a
      // NON-UNIFORM block INSERT (`scene3d/adapters/typed/blockTransform.ts
      // ::transformDimensionEllipseArc`). Params are CCW from the MAJOR AXIS
      // (already re-anchored and, for a mirrored insert, already swapped into
      // CCW order), which is exactly `ellipseToBeziers`' own convention — the
      // same exact-cubic helper `curveConverter.ts` draws every ELLIPSE with.
      //
      // COMPLETENESS ARM: the PDF pipeline builds its dimension primitives
      // straight off the ENTITY (`dimensionToPrimitives` above), which only
      // ever emits circular `arc`s, and its `insertConverter.ts` carries no
      // Dimension case at all — so no promoted primitive reaches this
      // converter today. It is here so that adding one cannot silently DROP
      // the arc, which is what an unhandled union member would do.
      const c = toRawPoint(p.center);
      const { start, segments } = ellipseToBeziers(
        c.x,
        c.y,
        p.majorAxis.x,
        p.majorAxis.y,
        p.ratio,
        p.startParam,
        p.endParam,
        true,
      );
      out.push({
        type: "path",
        entityId,
        layer,
        start,
        segments,
        closed: false,
        fill: "none",
        style: styleWithColor(baseStyle, p.colorHex),
      });
      return;
    }
    case "arrow": {
      const style = styleWithColor(baseStyle, p.colorHex);
      if (isFilledArrow(p.variant)) {
        // Solid arrowhead — closed FILL-ONLY path (stroke:false, nonzero) so
        // the backend emits `f` only, like a solid hatch (5a pattern).
        const poly = arrowFillPolygon(
          toRawPoint(p.tip),
          { x: p.direction.x, y: p.direction.y },
          p.size,
          p.variant,
        );
        if (poly.length >= 3) {
          out.push({
            type: "path",
            entityId,
            layer,
            start: poly[0]!,
            segments: poly.slice(1).map((pt) => ({ kind: "line", to: pt })),
            closed: true,
            fill: "nonzero",
            stroke: false,
            style,
          });
        }
        return;
      }
      // Open / blank variant — stroked outline (no fill).
      const poly = arrowOutlinePolyline(
        toRawPoint(p.tip),
        { x: p.direction.x, y: p.direction.y },
        p.size,
        p.variant,
      );
      const path = strokedPath(entityId, layer, poly, style);
      if (path) out.push(path);
      return;
    }
    case "tick": {
      // Oblique / architectural slash — a stroked line through the tip.
      const poly = tickPolyline(
        toRawPoint(p.tip),
        { x: p.direction.x, y: p.direction.y },
        p.size,
        p.variant,
      );
      const path = strokedPath(
        entityId,
        layer,
        poly,
        styleWithColor(baseStyle, p.colorHex),
      );
      if (path) out.push(path);
      return;
    }
    case "text": {
      const anchor = toRawPoint(p.position);
      const { hJust, vJust } = alignmentToCodes(p.alignment);
      out.push({
        type: "text-run",
        entityId,
        layer,
        position: anchor,
        height: p.height,
        rotation: p.rotation,
        widthFactor: 1,
        obliqueAngle: 0,
        hJustification: hJust,
        vJustification: vJust,
        styleName: p.styleName,
        value: p.content,
        color: hexToPlotRgb(p.colorHex),
        // GitHub Codex review PR #558 round 2 P2 — carry the MIRRTEXT
        // handedness flag into the PDF text-run IR (see `TextRunPrimitive
        // .mirrored`'s own doc); `undefined`/`false` is a no-op, matching
        // every text run before this field existed.
        mirrored: p.mirrored,
      });
      return;
    }
  }
}
